import { isGranted, UC_SERVICE_ID } from '../usercentrics';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
    Vimeo?: any;
  }
}

let video: any;

export const vimeo = ({ vimeoUrl }) => ({
  scriptInterval: undefined,
  dialog: {
    show: false,
    consent: false,
    init: false,
  },

  openDialog() {
    this.dialog.show = true;
    this.dialog.init = true;

    if (!this.dialog.consent) return;

    if (video) {
      video.play();
    } else {
      this.createPlayer();
    }
  },

  closeDialog() {
    this.dialog.show = false;
    if (video) {
      video.pause();
    }
  },

  /**
   * Creates the Vimeo player. Usercentrics only unblocks the player script once
   * consent has been given, so `window.Vimeo` may still be missing right after
   * the user accepted — wait for it instead of silently doing nothing.
   */
  createPlayer() {
    if (video) return;
    if (this.scriptInterval) clearInterval(this.scriptInterval);

    if (window.Vimeo) {
      this.initPlayer();
      return;
    }

    this.scriptInterval = setInterval(() => {
      if (!window.Vimeo) return;
      clearInterval(this.scriptInterval);
      this.scriptInterval = undefined;
      this.initPlayer();
    }, 200);
  },

  initPlayer() {
    video = new window.Vimeo.Player(this.$refs.video, {
      url: vimeoUrl,
      vimeo_logo: false,
      // responsive: true,
      // loop: true,
    });

    video.on(
      'play',
      async (data: { duration: number; percent: number; seconds: number }) => {
        if (data === null) return;
        const currentTime = Number(data.seconds.toFixed());
        const duration = await video.getDuration();
        const videoTitle = await video.getVideoTitle();
        this.trackEconda('playing', {
          currentTime,
          duration,
          videoTitle,
        });
      }
    );
    video.on(
      'pause',
      async (data: { duration: number; percent: number; seconds: number }) => {
        const currentTime = Number(data.seconds.toFixed());
        const duration = await video.getDuration();
        const videoTitle = await video.getVideoTitle();
        if (length >= currentTime)
          this.trackEconda('paused', {
            currentTime,
            duration,
            videoTitle,
          });
      }
    );
    video.on(
      'ended',
      async (data: { duration: number; percent: number; seconds: number }) => {
        const currentTime = Number(data.seconds.toFixed());
        const duration = await video.getDuration();
        const videoTitle = await video.getVideoTitle();
        this.trackEconda('stopped', {
          currentTime,
          duration,
          videoTitle,
        });
      }
    );
  },

  /** Open Usercentrics UI and display Vimeo-Player details. */
  moreInfo() {
    window.__ucCmp?.showServiceDetails(UC_SERVICE_ID.vimeo);
  },

  /** Accepts Usercentrics Vimeo-Player. */
  async accept() {
    if (!window.__ucCmp) return;
    await window.__ucCmp.updateServicesConsents([
      { id: UC_SERVICE_ID.vimeo, consent: true },
    ]);
    await window.__ucCmp.saveConsents('EXPLICIT');
    this.setConsent(true);
  },

  setConsent(granted: boolean) {
    if (granted === this.dialog.consent) return;
    this.dialog.consent = granted;

    if (!granted) {
      if (this.scriptInterval) clearInterval(this.scriptInterval);
      this.scriptInterval = undefined;
      video = undefined;
      return;
    }

    // Consent was given while the dialog is already open — start the player.
    if (this.dialog.show) this.createPlayer();
  },

  /**
   * Reads the current consent from the CMP. `__ucCmp` is only available once
   * the Usercentrics loader has initialized.
   */
  async syncConsent() {
    if (!window.__ucCmp) {
      window.addEventListener('UC_UI_INITIALIZED', () => this.syncConsent(), {
        once: true,
      });
      return;
    }

    this.setConsent(
      isGranted(await window.__ucCmp.getConsentDetails(), UC_SERVICE_ID.vimeo)
    );
  },

  init() {
    // UC_CONSENT fires with the initial consent state and on every change.
    window.addEventListener('UC_CONSENT', (e: any) => {
      this.setConsent(isGranted(e.detail, UC_SERVICE_ID.vimeo));
    });

    this.syncConsent();
  },

  trackEconda(
    state: 'playing' | 'paused' | 'stopped',
    data: {
      currentTime: number;
      duration: number;
      videoTitle: string;
    }
  ) {
    if (!window.dataLayer) return;
    window.dataLayer.push({
      event: 'RichMedia',
      event_name: 'rich_media',
      content: {
        name: data.videoTitle,
        category: 'Sonstiges Video',
      },
      type: 'video',
      duration: data.duration,
      previewUri: vimeoUrl,
      state: [state, data.currentTime],
    });
  },
});
